<template>
  <q-card
    v-bind="$attrs"
    class="text-left text-decoration-none block"
    :class="{
      'q-px-md q-py-sm bg-responsive text-responsive': !showSiteImage,
      [cardColorCss]: showSiteImage
    }"
    :style="{
      'background-color': cardColor === 'custom' ? cardColorHex : undefined,
      'border-radius': showSiteImage ? `${cardBorderRadius}px` : undefined
    }"
    flat
    tag="a"
    :href="siteHref"
    data-test-id="site-location-zone-card"
    @click.prevent="navigateAndTrackSiteSelection"
  >
    <q-img
      v-if="showSiteImage && site.siteThumbnailImageUrl"
      :src="site.siteThumbnailImageUrl"
      :alt="`picture of ${site.name}`"
      :ratio="3/2"
      :style="{ 'border-radius': cardColor === 'default' ? `${cardBorderRadius}px` : undefined }"
    />
    <q-card-section
      class="column"
      :class="{
        'q-pa-none': !showSiteImage,
        'q-px-none': cardColor === 'default'
      }"
    >
      <div class="text-subtitle1 text-bold inline-block full-width">
        {{ siteName }}
      </div>
      <template v-if="!hideAddress">
        <div
          class="text-subtitle2"
          style="opacity: 0.6;"
        >
          {{ site.address1 }}
        </div>
        <div
          class="text-subtitle2"
          style="opacity: 0.6;"
        >
          {{ site.addressShort }}
        </div>
      </template>

      <div
        v-if="showSiteDescription && !!site.siteDescription"
        class="text-body q-mt-md"
      >
        {{ site.siteDescription }}
      </div>
    </q-card-section>
  </q-card>
</template>

<script>
import Site from 'src/models/site/Site'
import { deletePrefix } from 'src/router/utils'

export default {
  name: 'SiteLocationZoneCard',
  configuration: ['currentSite'],
  props: {
    site: {
      type: Site,
      required: true
    },
    showDistance: {
      type: Boolean,
      required: false,
      default: false
    },
    hideAddress: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteImage: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteDescription: {
      type: Boolean,
      required: false,
      default: false
    },
    cardColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    cardColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    cardBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    redirectHomePage: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['select'],
  computed: {
    siteName () {
      return `${this.site.name} ${(this.showDistance && this.site.currentDistanceText) ? '(' + this.site.currentDistanceText + ')' : ''}`
    },
    siteHref () {
      if (this.redirectHomePage) {
        return this.$appendCurrentQueryParams(`${this.site.url()}?preferred`)
      }

      let currentPath = window.location.pathname
      if (this.currentSite.rootPath !== '/' && _.startsWith(currentPath, this.currentSite.rootPath)) {
        currentPath = deletePrefix(currentPath, this.currentSite.rootPath)
      }
      return this.$appendCurrentQueryParams(`${this.site.url(currentPath)}?preferred`)
    },
    cardColorCss () {
      if (this.cardColor === 'custom') {
        return this.$isColorDark(this.cardColorHex) ? 'text-white' : 'text-black'
      } else if (this.cardColor === 'default') {
        return 'text-responsive bg-transparent'
      } else {
        return `bg-${this.cardColor} ${this.$getTextColor(this.currentSite, this.cardColor)}`
      }
    }
  },
  methods: {
    // TODO: This component should be refactored to use InternalOrExternalLink component instead of
    // handling the navigation via this click handler, but making this change breaks CSS styling in
    // different places. This is a temporary workaround to get vue-router navigation working at the
    // same time as keeping the `<a>` tag for SEO and usability.
    navigateAndTrackSiteSelection () {
      this.$trackEvent(this.$customerEvents.CLICKED_SITE_SELECTOR_ON_NAVIGATION)
      if (this.$isUrlSameHostName(this.siteHref)) {
        this.$emit('select')
        const parsedUrl = new URL(this.siteHref)
        this.$router.push(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`)
      } else {
        window.location.href = this.siteHref
      }
    }
  }
}
</script>

<style scoped>
.site-location-zone-card {
  display: block;
  text-decoration: none;
  color: inherit;
}
</style>
